/*
Genre detail screen — fiche-genre. Mirrors Android GenreDetailScreen.kt
introduced in v1.1.0 « Catalogue ».

Sections (top to bottom):
- IdentityBlock        : nom FR (fr) + binôme latin (g) italique.
- WikipediaBlock       : summary + lien fr.wikipedia.org/wiki/{wp}.
- StatsBlock « À Paris » : count + proportion + médianes h/circ + top 3 arrondissements.
- GenreCatalogueHeader : « Espèces du genre · N / Y capturées ».
- 3-column grid        : une cellule par espèce identifiée du genre, triée par
                          pokedexNumber. Capturée → #N + nom + thumb cliquable.
                          Non-capturée → silhouette ??? non-cliquable.
- PhotoGallery « Tes captures à espèce indéterminée » : visible quand le user
                          a au moins une capture de la (G, sp.) du genre.
- ShowOnMapButton      : navigate kPaths.mapFiltered(genreFilterSet) — carte
                          filtrée au set `{sks sp.} ∪ {sks identifiés capturés}`.

Verrou : l'écran est accessible seulement si l'utilisateur a au moins une
capture du genre (identifiée OU sp.). Le verrou est revérifié après chaque
suppression depuis la galerie sp. — si la dernière capture du genre part,
on retombe sur la carte (parité Android `onUnlockLost()`).
*/

import { navigate, kPaths, type Route } from '../router'
import {
	ensureSpeciesIndex,
	allSpecies,
	displayName,
	formatLatin,
	genreFilterSet,
	isActiveSpecies,
	type SpeciesName,
} from '../data/speciesIndex'
import { ensureGenreInfo, genreInfoFor, type GenreEntry } from '../data/genreInfo'
import { ensureSpeciesPhotos, photosFor, photoUrl } from '../data/speciesPhotos'
import { listCaptures, deleteCapture } from '../data/captureRepository'
import { loadPhoto } from '../data/photoStore'
import { mountPhotoGallery, type PhotoGalleryItem, type PhotoGalleryController } from './components/PhotoGallery'
import { openLightbox, type LightboxPhoto } from './components/PhotoLightbox'
import { confirmDeleteCapture } from './components/DeleteCaptureDialog'
import { renderSkeletonBlock, renderSkeletonLines } from './components/Skeleton'
import { renderIconPlataneSvg } from './components/IconPlatane'
import type { Capture } from '../data/types'

const kFrenchNumber = new Intl.NumberFormat('fr-FR')
const kPercentFr = new Intl.NumberFormat('fr-FR', { style: 'percent', minimumFractionDigits: 1, maximumFractionDigits: 2 })

interface ViewModel
{
	genre: string  // latin genus, the route param
	info: GenreEntry | null  // null when the dataset doesn't carry this genus (rare)
	// refPhotoUrl : photo de référence de l'espèce (URL statique /data/) prioritaire
	// sur la 1re capture — parité Android CatalogueCell.kt. Mutuellement exclusif
	// avec thumbFilename : si refPhotoUrl, thumbFilename reste null pour que
	// l'hydratation IDB saute la cellule.
	identifiedSpecies: Array<{ sk: number; name: SpeciesName; captured: boolean; refPhotoUrl: string | null; thumbFilename: string | null }>
	unknownSpeciesSks: number[]  // (G, sp.) entries of the genus — usually 0 or 1
	unknownCaptures: Capture[]  // captures whose speciesIndex ∈ unknownSpeciesSks, sorted most-recent first
	loadError: string | null
}

let fActiveObjectUrls: string[] = []  // photo Blob URLs created by the current mount, revoked on remount
let fActiveGallery: PhotoGalleryController | null = null  // mounted PhotoGallery, destroyed on remount

/*
Mount the genre detail screen for the route's `genreName`. Renders a static
shell synchronously, hydrates async with the verrou check.
*/
export function mountGenreDetailScreen(inRoot: HTMLElement, inRoute: Route): void
{
	revokeObjectUrls()
	if (fActiveGallery) { fActiveGallery.destroy(); fActiveGallery = null }

	if (inRoute.id !== 'genre')
	{
		inRoot.innerHTML = '<div class="screen"><div class="placeholder">Route invalide.</div></div>'
		return
	}
	const vGenre = inRoute.genreName

	inRoot.innerHTML = renderShell(vGenre)
	inRoot.querySelector('#nav-back')?.addEventListener('click', () => {
		if (history.length > 1) history.back()
		else navigate(kPaths.arboretum())
	})
	void hydrate(inRoot, vGenre)
}

function revokeObjectUrls(): void
{
	for (const vUrl of fActiveObjectUrls) URL.revokeObjectURL(vUrl)
	fActiveObjectUrls = []
}

function renderShell(inGenre: string): string
{
	const vSkeleton = `
		<section class="species-card species-card-skeleton">
			<div class="species-skeleton-identity">
				${renderSkeletonBlock({ width: '60px', height: '60px', radius: '50%' })}
				<div class="species-skeleton-identity-text">
					${renderSkeletonBlock({ width: '70%', height: '20px' })}
					${renderSkeletonBlock({ width: '45%', height: '13px' })}
				</div>
			</div>
		</section>
		<section class="species-card species-card-skeleton">
			${renderSkeletonBlock({ width: '38%', height: '15px' })}
			<div class="species-skeleton-stack">${renderSkeletonLines(3, '12px')}</div>
		</section>
	`
	return `
		<div class="screen species">
			<header class="arboretum-header">
				<button class="nav-back" id="nav-back" aria-label="Retour">←</button>
				<h1 class="display arboretum-title">${escapeHtml(inGenre)}</h1>
			</header>
			<div class="species-body" id="genre-body">
				${vSkeleton}
			</div>
		</div>
	`
}

async function hydrate(inRoot: HTMLElement, inGenre: string): Promise<void>
{
	const vBody = inRoot.querySelector<HTMLElement>('#genre-body')
	if (!vBody) return

	const vModel = await loadViewModel(inGenre)

	if (vModel.loadError)
	{
		vBody.innerHTML = `<div class="placeholder species-error">⚠ ${escapeHtml(vModel.loadError)}</div>`
		return
	}

	// Verrou : aucune capture du genre (identifiée OU sp.) → redirect map.
	// Parité Android `onUnlockLost()` qui pop la page si la garde réactive
	// se déclenche.
	const vHasIdentifiedCapture = vModel.identifiedSpecies.some((inS) => inS.captured)
	const vHasUnknownCapture = vModel.unknownCaptures.length > 0
	if (!vHasIdentifiedCapture && !vHasUnknownCapture)
	{
		navigate(kPaths.map())
		return
	}

	vBody.innerHTML = renderViewModel(vModel)

	wireGrid(vBody)
	wireMapButton(vBody, vModel)
	void hydrateThumbnails(vBody, vModel.identifiedSpecies)
	if (vModel.unknownCaptures.length > 0)
	{
		void hydrateUnknownGallery(inRoot, vBody, inGenre, vModel)
	}
}

async function loadViewModel(inGenre: string): Promise<ViewModel>
{
	try
	{
		// ensureSpeciesPhotos ne throw jamais (asset absent → photosFor null,
		// retombée sur les thumbs capture — parité SpeciesPhotoRepository).
		const [, , , vCaptures] = await Promise.all([
			ensureSpeciesIndex(),
			ensureGenreInfo(),
			ensureSpeciesPhotos(),
			listCaptures(),
		])
		const vInfo = genreInfoFor(inGenre)  // may be null if the dataset doesn't carry this genus

		// Partition the genus's species rows into active vs (G, sp.).
		// The dataset may have multiple `sp.` rows per genus historically;
		// we accept all of them and pool their captures into one gallery.
		// v1.3.1 : zombie rows (identified species with pokedexNumber===null,
		// count=0 in the current OpenData snapshot) are excluded — they
		// would render as locked "—" cells nobody can ever capture.
		const vAll = allSpecies()
		const vIdentified: Array<{ sk: number; name: SpeciesName }> = []
		const vUnknownSks: number[] = []
		for (const vEntry of vAll)
		{
			if (vEntry.name.genre !== inGenre) continue
			if (vEntry.name.unknownSpecies) vUnknownSks.push(vEntry.sk)
			else if (isActiveSpecies(vEntry.name)) vIdentified.push({ sk: vEntry.sk, name: vEntry.name })
		}
		// Sort identified species by pokedex rank ascending (frozen at build
		// time by Paris count desc). Rows without a rank sink to the bottom.
		vIdentified.sort((inA, inB) => {
			const vRa = inA.name.pokedexNumber ?? Number.MAX_SAFE_INTEGER
			const vRb = inB.name.pokedexNumber ?? Number.MAX_SAFE_INTEGER
			if (vRa !== vRb) return vRa - vRb
			return formatLatin(inA.name).localeCompare(formatLatin(inB.name), 'fr')
		})

		// Build captured-set + thumbnail-filename for each identified row.
		const vBySk = new Map<number, Capture[]>()  // speciesIndex -> captures (we'll only need the most recent for the thumb)
		for (const vCap of vCaptures)
		{
			const vBucket = vBySk.get(vCap.speciesIndex) ?? []
			vBucket.push(vCap)
			vBySk.set(vCap.speciesIndex, vBucket)
		}
		// Anti-spoiler structurel (parité CatalogueCell.kt) : photosFor n'est
		// consulté QUE pour les espèces capturées — aucune URL de référence dans
		// le DOM d'une espèce non capturée. Perf : le mini-catalogue genre ne
		// compte au plus que quelques dizaines d'espèces capturées portant une
		// URL (WebP ~60 Ko, SW cache-first /data/) — pas d'IntersectionObserver.
		const vIdentifiedRows = vIdentified.map((inE) => {
			const vCaps = vBySk.get(inE.sk) ?? []
			const vCaptured = vCaps.length > 0
			const vRefPhotos = vCaptured ? photosFor(inE.sk) : null
			const vRefUrl = vRefPhotos ? photoUrl(vRefPhotos.principal) : null
			let vThumb: string | null = null
			// Photo de réf. prioritaire : si elle existe, on ne calcule PAS le
			// thumb capture (mutuellement exclusifs → l'hydratation IDB saute).
			if (vCaptured && !vRefUrl)
			{
				// Pick the most recent capture's photo as the thumb. Same
				// rule as the Arboretum card strip.
				const vSorted = vCaps.slice().sort((inA, inB) => inB.timestamp - inA.timestamp)
				for (const vCap of vSorted) { if (vCap.photoFilename) { vThumb = vCap.photoFilename; break } }
			}
			return { sk: inE.sk, name: inE.name, captured: vCaptured, refPhotoUrl: vRefUrl, thumbFilename: vThumb }
		})

		const vUnknownSet = new Set(vUnknownSks)
		const vUnknownCaptures = vCaptures
			.filter((inC) => vUnknownSet.has(inC.speciesIndex))
			.sort((inA, inB) => inB.timestamp - inA.timestamp)

		return {
			genre: inGenre,
			info: vInfo,
			identifiedSpecies: vIdentifiedRows,
			unknownSpeciesSks: vUnknownSks,
			unknownCaptures: vUnknownCaptures,
			loadError: null,
		}
	}
	catch (vErr)
	{
		const vMsg = vErr instanceof Error ? vErr.message : 'Erreur inconnue'
		console.error('GenreDetail hydration failed', vErr)
		return {
			genre: inGenre,
			info: null,
			identifiedSpecies: [],
			unknownSpeciesSks: [],
			unknownCaptures: [],
			loadError: vMsg,
		}
	}
}

function renderViewModel(inModel: ViewModel): string
{
	const vInfo = inModel.info
	const vTitle = vInfo?.fr ?? inModel.genre  // user-facing display: French name when present, else latin
	const vSubtitle = vInfo?.fr ? inModel.genre : null  // when French name is the title, the latin sits underneath

	const vIdentity = `
		<section class="species-card">
			<div class="species-glyph" aria-hidden="true">🌿</div>
			<div class="species-name">
				<h2 class="display species-title">${escapeHtml(vTitle)}</h2>
				${vSubtitle ? `<div class="species-latin"><em>${escapeHtml(vSubtitle)}</em></div>` : ''}
			</div>
		</section>
	`

	const vWiki = vInfo?.summary ? renderWiki(vInfo.summary, vInfo.wp ?? null) : ''
	const vStats = vInfo ? renderStats(vInfo) : ''
	const vCatalogue = renderCatalogue(inModel)
	const vGallery = inModel.unknownCaptures.length > 0 ? `
		<section class="species-card">
			<div id="genre-unknown-gallery"></div>
		</section>
	` : ''
	const vMapBtn = `
		<section class="species-card">
			<button class="species-action" id="genre-show-on-map">🗺️ Voir sur la carte</button>
		</section>
	`

	return `${vIdentity}${vWiki}${vStats}${vCatalogue}${vGallery}${vMapBtn}`
}

function renderWiki(inSummary: string, inSlug: string | null): string
{
	const vLink = inSlug ? `
		<a class="species-link" href="https://fr.wikipedia.org/wiki/${encodeURIComponent(inSlug)}" target="_blank" rel="noopener">
			Lire sur Wikipédia →
		</a>
	` : ''
	return `
		<section class="species-card species-card-wiki">
			<h3 class="species-section-title">Wikipédia</h3>
			<p class="species-wiki-text">${escapeHtml(inSummary)}</p>
			${vLink}
			<div class="species-wiki-attribution">Source : Wikipédia FR · CC BY-SA 4.0</div>
		</section>
	`
}

function renderStats(inInfo: GenreEntry): string
{
	const vStats = inInfo.stats
	const vCount = kFrenchNumber.format(vStats.count)
	const vProp = typeof vStats.proportion === 'number' ? kPercentFr.format(vStats.proportion) : null
	const vTopArr = vStats.topArr.slice(0, 3)
	const vBars = vTopArr.map((inA) => {
		const vMaxCount = vTopArr[0]?.count ?? 1
		const vPct = (inA.count / vMaxCount) * 100
		return `
			<div class="species-bar-row">
				<span class="species-bar-label">${escapeHtml(inA.arr)}</span>
				<span class="species-bar-track"><span class="species-bar-fill" style="width:${vPct.toFixed(0)}%"></span></span>
				<span class="species-bar-count">${kFrenchNumber.format(inA.count)}</span>
			</div>
		`
	}).join('')

	const vMedians: string[] = []
	if (typeof vStats.medianHm === 'number' && vStats.medianHm > 0)
	{
		vMedians.push(`<div class="species-stat-line">📏 Hauteur médiane : <strong>${vStats.medianHm} m</strong></div>`)
	}
	if (typeof vStats.medianCircCm === 'number' && vStats.medianCircCm > 0)
	{
		vMedians.push(`<div class="species-stat-line">⭕ Circonférence médiane : <strong>${vStats.medianCircCm} cm</strong></div>`)
	}

	const vHeadline = vProp
		? `<strong>${vCount}</strong> arbres · <strong>${vProp}</strong> du dataset`
		: `<strong>${vCount}</strong> arbres à Paris`
	const vSpeciesIdentified = vStats.speciesIdentified > 0
		? `<div class="species-stat-line"><strong>${vStats.speciesIdentified}</strong> ${vStats.speciesIdentified > 1 ? 'espèces identifiées' : 'espèce identifiée'}</div>`
		: ''

	return `
		<section class="species-card">
			<h3 class="species-section-title">À Paris</h3>
			<div class="species-stat-line">${vHeadline}</div>
			${vSpeciesIdentified}
			${vMedians.join('')}
			${vBars ? `<div class="species-bars">${vBars}</div>` : ''}
		</section>
	`
}

function renderCatalogue(inModel: ViewModel): string
{
	const vTotal = inModel.identifiedSpecies.length
	const vCaptured = inModel.identifiedSpecies.filter((inS) => inS.captured).length
	const vCells = inModel.identifiedSpecies.map(renderCatalogueCell).join('')

	if (vTotal === 0)
	{
		return ''  // genus with no identified species (rare; only-unknown like Genista/Vitex/Ziziphus)
	}

	return `
		<section class="species-card">
			<h3 class="species-section-title">
				Espèces du genre · <strong>${vCaptured}</strong> / ${vTotal} ${vTotal > 1 ? 'capturées' : 'capturée'}
			</h3>
			<div class="genre-catalogue-grid">${vCells}</div>
		</section>
	`
}

function renderCatalogueCell(inSpecies: { sk: number; name: SpeciesName; captured: boolean; refPhotoUrl: string | null; thumbFilename: string | null }): string
{
	const vRank = inSpecies.name.pokedexNumber !== null ? `#${inSpecies.name.pokedexNumber}` : ''
	if (!inSpecies.captured)
	{
		// Locked silhouette: rank stays visible when present (so the user can
		// see where in the Pokédex the unseen species sits), the latin is
		// hidden under "???" — parity with Android CatalogueCell.
		return `
			<div class="catalogue-cell catalogue-cell-locked" aria-disabled="true">
				<div class="catalogue-cell-thumb catalogue-cell-thumb-locked" aria-hidden="true">???</div>
				<div class="catalogue-cell-rank">${vRank}</div>
				<div class="catalogue-cell-name">???</div>
			</div>
		`
	}
	const vTitle = displayName(inSpecies.name)
	// Priorité 1 (parité CatalogueCell.kt l.87-93) : photo de référence rendue
	// DIRECTEMENT en background-image (URL statique, même rendu final que
	// l'hydratation IDB via `.catalogue-cell-thumb-image`). Pas de
	// data-thumb-filename → hydrateThumbnails saute la cellule.
	// Priorité 2 : thumbnail 1re capture via l'hydratation IDB (placeholder + attribut).
	const vThumbHtml = inSpecies.refPhotoUrl
		? `<div class="catalogue-cell-thumb catalogue-cell-thumb-image" style="background-image:url('${escapeAttr(inSpecies.refPhotoUrl)}')" aria-hidden="true"></div>`
		: `<div class="catalogue-cell-thumb" data-thumb-filename="${escapeAttr(inSpecies.thumbFilename ?? '')}" aria-hidden="true">${renderIconPlataneSvg('catalogue-cell-thumb-icon')}</div>`
	return `
		<button class="catalogue-cell" data-species-index="${inSpecies.sk}" aria-label="${escapeAttr(vTitle)}">
			${vThumbHtml}
			<div class="catalogue-cell-rank">${vRank}</div>
			<div class="catalogue-cell-name">${escapeHtml(vTitle)}</div>
		</button>
	`
}

function wireGrid(inBody: HTMLElement): void
{
	inBody.querySelectorAll<HTMLElement>('.catalogue-cell[data-species-index]').forEach((inCell) => {
		inCell.addEventListener('click', () => {
			const vSk = Number(inCell.dataset.speciesIndex)
			if (Number.isFinite(vSk)) navigate(kPaths.species(vSk))
		})
	})
}

function wireMapButton(inBody: HTMLElement, inModel: ViewModel): void
{
	// genreFilterSet (parité Android GenreDetailScreen.kt) : les entrées
	// (G, sp.) du genre — même non capturées, c'est « ce que j'ai à
	// résoudre » — plus chaque espèce identifiée déjà capturée, « mes
	// trophées du genre ». v1.5.0 : helper partagé avec les filtres
	// rapides de la sheet (speciesIndex.genreFilterSet).
	const vCapturedSks = new Set(inModel.identifiedSpecies.filter((inS) => inS.captured).map((inS) => inS.sk))
	const vSet = genreFilterSet(inModel.genre, vCapturedSks)
	const vBtn = inBody.querySelector<HTMLButtonElement>('#genre-show-on-map')
	if (!vBtn) return
	if (vSet.length === 0)
	{
		vBtn.disabled = true  // parité Android enabled = genreFilterSet.isNotEmpty()
		return
	}
	vBtn.addEventListener('click', () => navigate(kPaths.mapFiltered(vSet)))
}

async function hydrateThumbnails(
	inBody: HTMLElement,
	inSpecies: ViewModel['identifiedSpecies'],
): Promise<void>
{
	for (const vEntry of inSpecies)
	{
		if (!vEntry.captured || !vEntry.thumbFilename) continue
		const vCell = inBody.querySelector<HTMLElement>(`.catalogue-cell[data-species-index="${vEntry.sk}"] .catalogue-cell-thumb`)
		if (!vCell) continue
		const vBlob = await loadPhoto(vEntry.thumbFilename)
		if (!vBlob) continue
		const vUrl = URL.createObjectURL(vBlob)
		fActiveObjectUrls.push(vUrl)
		vCell.style.backgroundImage = `url("${vUrl}")`
		vCell.classList.add('catalogue-cell-thumb-image')
		vCell.textContent = ''
	}
}

async function hydrateUnknownGallery(
	inRoot: HTMLElement,
	inBody: HTMLElement,
	inGenre: string,
	inModel: ViewModel,
): Promise<void>
{
	const vHost = inBody.querySelector<HTMLElement>('#genre-unknown-gallery')
	if (!vHost) return

	// Build the gallery items: load each photo Blob, accumulate URLs that
	// the PhotoGallery component then renders + the lightbox can consume.
	const vItems: PhotoGalleryItem[] = []
	const vLightboxPhotos: LightboxPhoto[] = []  // mirrors vItems by index; passed to openLightbox
	const vDisplayName = inModel.info?.fr ? `${inModel.info.fr} (espèce indéterminée)` : `${inGenre} (espèce indéterminée)`
	for (const vCap of inModel.unknownCaptures)
	{
		if (!vCap.photoFilename) continue
		if (typeof vCap.id !== 'number') continue
		const vBlob = await loadPhoto(vCap.photoFilename)
		if (!vBlob) continue
		const vUrl = URL.createObjectURL(vBlob)
		fActiveObjectUrls.push(vUrl)
		vItems.push({ captureId: vCap.id, url: vUrl })
		vLightboxPhotos.push({
			url: vUrl,
			arbreId: vCap.arbreId,
			captureId: vCap.id,
			lat: vCap.lat ?? undefined,  // Capture.lat is number|null; LightboxPhoto expects number|undefined
			lon: vCap.lon ?? undefined,
		})
	}

	if (vItems.length === 0)
	{
		// All photos failed to load — clean up the empty card so the layout
		// doesn't end on a useless shell.
		vHost.parentElement?.remove()
		return
	}

	const vTitle = `Tes captures à espèce indéterminée · ${vItems.length}`

	fActiveGallery = mountPhotoGallery({
		host: vHost,
		title: vTitle,
		items: vItems,
		onThumbClick: (inIndex) => {
			openLightbox(vLightboxPhotos, inIndex, {
				onJumpToMap: (inActiveIndex) => {
					const vPhoto = vLightboxPhotos[inActiveIndex]
					if (vPhoto?.arbreId !== undefined) navigate(kPaths.map(vPhoto.arbreId))
				},
				onDelete: async (inActiveIndex) => {
					const vPhoto = vLightboxPhotos[inActiveIndex]
					if (!vPhoto || typeof vPhoto.captureId !== 'number') return false
					return handleUnknownDelete(inRoot, inGenre, vPhoto.captureId, vDisplayName, vLightboxPhotos.length === 1)
				},
			})
		},
		onThumbLongPress: async (inIndex) => {
			const vItem = vItems[inIndex]
			if (!vItem) return
			await handleUnknownDelete(inRoot, inGenre, vItem.captureId, vDisplayName, vItems.length === 1)
		},
	})
}

/*
Shared delete path used by both lightbox trash icon and gallery
long-press. Confirms with the dialog, deletes from IDB, then re-hydrates
the screen so the gallery + verrou react together. Returns whether the
delete went through (the lightbox uses this to decide if it advances).
*/
async function handleUnknownDelete(
	inRoot: HTMLElement,
	inGenre: string,
	inCaptureId: number,
	inDisplayName: string,
	inIsLast: boolean,
): Promise<boolean>
{
	const vConfirmed = await confirmDeleteCapture({
		kind: 'species',
		displayName: inDisplayName,
		isLast: inIsLast,
	})
	if (!vConfirmed) return false
	try
	{
		await deleteCapture(inCaptureId)
	}
	catch (vErr)
	{
		console.error('deleteCapture failed', vErr)
		return false
	}
	// Re-hydrate the screen. If the verrou drops (zero captures for the
	// genre left), hydrate() will redirect to the map itself.
	void hydrate(inRoot, inGenre)
	return true
}

function escapeHtml(inText: string): string
{
	return inText
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;')
}

function escapeAttr(inText: string): string { return escapeHtml(inText) }
